import {
  Announcement,
  AnnouncementTarget,
  Box,
  Disclaimer,
  Div,
  EditPenIcon,
  Icon,
  Pill,
  TrashIconRed,
  lightTheme,
} from '@vgrubs/components';
import {
  RowActions,
  RowSummary,
  TargetRow,
  Title,
  TitleRow,
  TypeLabel,
} from './AnnouncementListItem.styled';
import { AnnouncementSeenBy } from './AnnouncementSeenBy';

type Props = {
  announcement: Announcement;
  /** The row itself: who saw it, on the details page. */
  onOpen: (id: number) => void;
  onEdit: (announcement: Announcement) => void;
  onDelete: (id: number) => void;
};

/**
 * How many targets get their own pill before the rest collapse into a count.
 *
 * An announcement aimed at every platform carries seven, which is more chips
 * than the row is tall and tells you nothing the first few did not. The full
 * set is one click away in the editor.
 */
const VISIBLE_TARGETS = 4;

const targetLabel = ({ app, platform }: AnnouncementTarget) =>
  `${app}/${platform}`;

export const AnnouncementListItem = ({
  announcement,
  onOpen,
  onEdit,
  onDelete,
}: Props) => {
  const { targets } = announcement;
  const shown = targets.slice(0, VISIBLE_TARGETS);
  const hidden = targets.length - shown.length;

  return (
    <Box
      $flex
      $alignCenter
      $between
      $gap="16px"
      $pointer
      onClick={() => onOpen(announcement.id)}
    >
      <RowSummary>
        <TitleRow>
          <Title>{announcement.title}</Title>
          <Pill
            $background={
              announcement.active ? lightTheme.green : lightTheme.lightGray
            }
            $minWidth="64px"
            $minWidthSM="64px"
          >
            <Disclaimer $m="0" $color={lightTheme.white}>
              {announcement.active ? 'Active' : 'Inactive'}
            </Disclaimer>
          </Pill>
        </TitleRow>

        <TargetRow>
          {/* <TypeLabel>{announcement.type}</TypeLabel> */}

          {shown.map((target) => (
            <Pill
              key={targetLabel(target)}
              $background={lightTheme.gray}
              $minWidth="0"
              $minWidthSM="0"
            >
              <Disclaimer $m="0" $color={lightTheme.darkGray}>
                {targetLabel(target)}
              </Disclaimer>
            </Pill>
          ))}

          {hidden > 0 && (
            <Pill $background={lightTheme.gray} $minWidth="0" $minWidthSM="0">
              <Disclaimer $m="0" $color={lightTheme.darkGray}>
                +{hidden}
              </Disclaimer>
            </Pill>
          )}

          {!targets.length && <TypeLabel>no targets</TypeLabel>}
        </TargetRow>

        {/* The faces need the width a phone row does not have; the count
            alone still answers "has anyone seen it". */}
        <Div $mobile $mt="6">
          <TypeLabel>
            {announcement.seenCount
              ? `Seen by ${announcement.seenCount}`
              : 'No views yet'}
          </TypeLabel>
        </Div>
      </RowSummary>

      <RowActions>
        <Div $desktop $mr="16">
          <AnnouncementSeenBy
            seenCount={announcement.seenCount}
            seenPreview={announcement.seenPreview}
          />
        </Div>
        {/* Both stopped here: the whole row opens the details page, and an
            edit or a delete that also navigated away would fight it. */}
        <Icon
          $pointer
          $shadow
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onEdit(announcement);
          }}
        >
          <EditPenIcon />
        </Icon>
        <Icon
          $pointer
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onDelete(announcement.id);
          }}
        >
          <TrashIconRed />
        </Icon>
      </RowActions>
    </Box>
  );
};
